<template>
  <div :class="['base-pill', { 'base-pill--link': isClickable }]">
    <component :is="component" :title="item.name" @click="handleClick" type="button" class="base-pill__link">
      <span class="base-pill__grid">
        <IconOnHover :is-disabled="!hasSecondaryAction" @click="hasSecondaryAction ? $emit('secondary-action') : null">
          <slot name="icon" />
        </IconOnHover>

        <span class="base-pill__name" :class="{ 'base-pill__name-full': showFullPillName }">
          {{ item.name }}
        </span>
      </span>
    </component>

    <span v-if="deletable" class="base-pill__delete">
      <span class="base-pill__delete-fade"></span>
      <!-- <slot name="delete" />
      <template v-if="!$slots.delete"> -->
      <AdaptiveColorButton color="#f4f4f5" show-close @click="$emit('delete')" />
      <!-- </template> -->
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AdaptiveColorButton from '@components/common/AdaptiveColorButton.vue';
import IconOnHover from '@components/IconOnHover.vue';

export default defineComponent({
  emits: { 'secondary-action': null, delete: null, click: null },
  name: 'BasePill',

  components: {
    IconOnHover,
    AdaptiveColorButton,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    deletable: {
      type: Boolean,
      default: false,
    },
    /**
     * The `hasSecondaryAction` prop indicates whether the pill has an additional action besides the primary click action.
     * When `hasSecondaryAction` is true, the IconOnHover component inside the pill becomes interactive and emits a 'secondary-action' event upon click.
     * This allows the parent component to listen for this event and perform any secondary behavior associated with this pill.
     * If `hasSecondaryAction` is false, the IconOnHover remains non-interactive and does not trigger any event.
     **/
    hasSecondaryAction: {
      type: Boolean,
      default: false,
    },

    clickable: {
      type: Boolean,
      default: false,
    },
    showFullPillName: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isClickable(): Boolean {
      return this.clickable;
    },

    component(): string {
      return this.isClickable ? 'button' : 'span';
    },
  },

  methods: {
    handleClick() {
      if (this.clickable) this.$emit('click');
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
.base-pill {
  position: relative;
  color: colors.$color-grey-800;
  --current-background-color: #f4f4f5;
}

.base-pill__link {
  display: block;
  border-radius: settings.$border-radius;
  white-space: nowrap;
  transition: all 0.1s ease-in-out;
  overflow: visible;
  background: var(--current-background-color);
  border: 1px solid colors.$color-grey-200;

  @include typography.typography-14-normal();
}

// show delete when hovering or focussing
.base-pill:hover,
.base-pill:focus-within {
  .base-pill__delete {
    opacity: 1;
  }
}

// hover state only if codepill is a button
.base-pill--link {
  .base-pill__link {
    &:hover {
      color: colors.$color-blue;
      box-shadow: settings.$box-shadow-border-blue;
    }

    &:focus {
      box-shadow: 0 0 0 3px colors.$color-blue-200;
    }
  }
}

.base-pill__grid {
  position: relative;
  height: 28px;
  display: grid;
  align-items: center;
  grid-gap: 8px;
  grid-auto-flow: column;
  padding: 0 10px;
}

.base-pill__name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;

  @include typography.typography-baseline-alignment;
}

.base-pill__name-full {
  overflow: visible;
}

.base-pill__delete {
  position: absolute;
  top: 0;
  right: 2px;
  opacity: 0;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.1s ease-in-out;
}

.base-pill__delete-fade {
  position: absolute;
  top: 1px;
  right: 0;
  bottom: 1px;
  width: 200%;
  pointer-events: none;
  overflow: hidden;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 50%;
    content: '';
    display: block;
    background: var(--current-background-color);
    box-shadow: 0 0 10px 10px var(--current-background-color);
    transition: all 0.1s ease-in-out;
  }
}
</style>
